import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserRound, ArrowUpRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PerfectSliceIcon } from '../brand/PerfectSliceBrand';
import './daily-badge.css';
import { toast } from 'sonner';
import { bootstrap, loadLocal, saveLocal, updateCachedNickname } from './api';
import { AuthDialog } from './AuthDialog';
import { formatPercent } from '../game/rules.mjs';
import { applyMetadata } from '../site/metadata';
import { useEndlessSync } from './useEndlessSync';
import { trackEvent, trackOnce, trackPageView } from '../lib/analytics';
import { cachedChallenge, fetchChallenge, fetchDailyState, playerIdentity, useUtcDate } from './dailyData';
import './platform.css';

const Context = createContext(null);
const legacyPaths = { '/daily': '/perfectslice/daily', '/leaderboard': '/perfectslice/leaderboard' };
const pathView = () => {
  const raw = window.location.pathname;
  const clean = raw.length > 1 ? raw.replace(/\/+$/, '') : raw;
  const path = legacyPaths[clean] || clean;
  if (path !== raw) window.history.replaceState({}, '', path + window.location.search + window.location.hash);
  return path;
};
export const usePlatform = () => useContext(Context);
export function PlatformProvider({ children }) {
  const [view, setView] = useState(pathView);
  const [user, commitUser] = useState(null);
  const [guest, setGuest] = useState(null);
  const authRevision = useRef(0);
  const refreshSerial = useRef(0);
  const setUser = useCallback(next => { authRevision.current += 1; commitUser(next); }, []);
  const [scope, setScope] = useState(null);
  const homeIdentity = playerIdentity(user, guest);
  const [authOpen, setAuthOpen] = useState(false);
  const [authKind, setAuthKind] = useState('signin');
  const [version, setVersion] = useState(0);
  const day = useUtcDate();
  const [daily, setDaily] = useState(() => { const challenge = cachedChallenge(); return challenge ? { challenge } : null; });
  const [authLoading, setAuthLoading] = useState(true);
  const [connectionError, setConnectionError] = useState('');
  const navigate = useCallback(path => {
    setAuthOpen(false);
    window.history.pushState({}, '', path);
    setView(pathView());
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const refreshAuth = useCallback(async () => {
    const serial = ++refreshSerial.current, revision = authRevision.current;
    try {
      const data = await bootstrap(true);
      if (serial === refreshSerial.current) {
        if (revision === authRevision.current) { commitUser(data.user); setGuest(data.guest || null); }
        setScope(data.scope); setConnectionError('');
      }
      return data.user;
    } catch (error) { if (serial === refreshSerial.current && revision === authRevision.current) setConnectionError(error.message); throw error; }
    finally { if (serial === refreshSerial.current) setAuthLoading(false); }
  }, []);
  const changed = useCallback(() => setVersion(v => v + 1), []);
  useEffect(() => { refreshAuth().catch(() => { /* refreshAuth exposes connectionError for the existing retry UI. */ }); }, [refreshAuth]);
  useEffect(() => { const pop = () => { setAuthOpen(false); setView(pathView()); }; window.addEventListener('popstate', pop); return () => window.removeEventListener('popstate', pop); }, []);
  useEffect(() => { applyMetadata(view); trackPageView(view); }, [view]);
  useEffect(() => { setDaily(current => current ? { challenge: current.challenge } : null); }, [user?.id, user?.alias_confirmed]);
  useEffect(() => {
    // The homepage primes the public payload; Daily consumes this same flight.
    // Neither the leaderboard nor arbitrary account routes fetch full Daily state.
    if (view !== '/perfectslice') return;
    let alive = true;
    const publicRequest = fetchChallenge();
    publicRequest.then(challenge => { if (alive) setDaily(current => current?.challenge.id === challenge.id ? { ...current, challenge } : { challenge }); }).catch(() => { /* Optional prefetch: DailySlice owns the visible fetch/retry state. */ });
    if (!authLoading) Promise.all([publicRequest, fetchDailyState(homeIdentity)]).then(([challenge, result]) => {
      if (alive && result.challenge_id === challenge.id && result.date === day) setDaily({ challenge, state: result.state, user: result.user });
    }).catch(() => { /* Optional sidebar state; never unlocks cutting or replaces verified Daily state. */ });
    return () => { alive = false; };
  }, [version, homeIdentity, view, day, authLoading]);
  const origin = useRef({ path: '/', direct: false });
  const authOpenRef = useRef(authOpen);
  authOpenRef.current = authOpen;
  const authenticated = useCallback(async (newUser, claim, options = {}) => {
    updateCachedNickname(user, newUser);
    if (newUser?.alias_confirmed && (claim?.claimed || claim?.kept_existing)) {
      const guest = loadLocal('perfect-slice.daily-cache.v1:guest');
      if (claim.claimed && !claim.kept_existing && guest?.state?.attempts?.length) {
        const saved = { ...guest, user: newUser, my_entry: null };
        saveLocal(`perfect-slice.daily-cache.v1:${newUser.id || newUser.alias}`, saved);
        saveLocal('perfect-slice.daily-last.v1', saved);
      }
      saveLocal('perfect-slice.daily-cache.v1:guest', null);
      const latest = loadLocal('perfect-slice.daily-last.v1');
      if (latest && !latest.user?.alias_confirmed) saveLocal('perfect-slice.daily-last.v1', null);
    }
    setUser(newUser);
    if (newUser?.alias_confirmed && !claim?.retry_needed) setGuest(null);
    const finished = options.finish !== false && newUser?.alias_confirmed && authOpenRef.current;
    // sign_up uses trackOnce so a retry of finishProfile cannot double-count.
    // login uses trackEvent — each intentional sign-in in a session is valid.
    if (options.created && newUser?.alias_confirmed) trackOnce('sign_up', `su:${newUser.id}`, {});
    else if (finished && !user?.alias_confirmed) trackEvent('login');
    // guest_claim fires when anonymous history is successfully merged into the account.
    // trackOnce deduped by user ID: only one claim event per registered user per session.
    if (claim?.claimed && newUser?.alias_confirmed) {
      trackOnce('guest_claim', `gc:${newUser.id}`, { kept_existing: false });
    } else if (claim?.kept_existing && newUser?.alias_confirmed) {
      trackOnce('guest_claim', `gc:${newUser.id}:ke`, { kept_existing: true });
    }
    if (finished) {
      setAuthOpen(false);
      if (origin.current.direct && window.location.pathname === '/signin') navigate(origin.current.path);
      toast(options.created ? `Welcome to CubitPlay, ${newUser.alias}` : `Welcome back, ${newUser.alias}`);
    }
    if (claim?.kept_existing) toast('Your account already has today’s attempts', { description: 'We kept that original Daily result. No attempts were replaced.' });
    if (claim?.retry_needed) toast('Your guest result is safe', { description: 'We’ll retry saving it to your account when you continue.' });
    changed();
    // Refresh the cached session response, not the game or route. Keep the
    // verified user if a follow-up network request happens to fail.
    await bootstrap(true).catch(() => { /* Sign-in already succeeded; later requests retry CSRF bootstrap without undoing it. */ });
  }, [user, setUser, navigate, changed]);
  const openAuth = useCallback((kind = 'signin') => {
    setAuthKind(kind === 'invite' ? 'invite' : 'signin');
    const current = pathView();
    const next = new URLSearchParams(window.location.search).get('next');
    let returnPath = current;
    if (current === '/signin') returnPath = ['/', '/account', '/perfectslice', '/perfectslice/daily', '/perfectslice/leaderboard'].includes(next) ? next : '/';
    origin.current = { direct: current === '/signin', path: returnPath };
    setAuthOpen(true);
  }, []);
  const endless = useEndlessSync(user, scope, changed);
  const closeAuth = useCallback(next => setAuthOpen(next), []);
  const contextValue = useMemo(() => ({ view, day, navigate, user, setUser, guest, setGuest, scope, daily, setDaily, authLoading, connectionError, refreshAuth, version, changed, openAuth, authOpen, authenticated, endless }), [view, day, navigate, user, setUser, guest, setGuest, scope, daily, authLoading, connectionError, refreshAuth, version, changed, openAuth, authOpen, authenticated, endless]);
  return <Context.Provider value={contextValue}>
    {children}
    <AuthDialog open={authOpen} kind={authKind} onOpenChange={closeAuth} user={user} guest={guest} onAuthenticated={authenticated}/>
  </Context.Provider>;
}
export function AuthEntry() {
  const { user, guest, openAuth, navigate } = usePlatform();
  let label = guest ? guest.alias : 'SIGN IN';
  if (user) label = user.alias_confirmed ? user.alias : 'FINISH PROFILE';
  return <button className="platform-auth-entry" data-testid="platform-sign-in-button" onClick={() => user?.alias_confirmed || (!user && guest) ? navigate('/account') : openAuth()} aria-label={user ? `Account: ${user.alias}` : guest ? `Browser profile: ${guest.alias}` : 'Sign in — optional'}><UserRound size={17}/><span>{label}</span></button>;
}
export function DailyHomeEntry() {
  const { daily, navigate } = usePlatform();
  const current = daily?.challenge?.date === new Date().toISOString().slice(0, 10) ? daily : null;
  const completed = !!current?.state?.completed, best = current?.state?.best, rank = current?.my_entry?.rank;
  const played = current?.state?.attempts?.length || 0;
  const href = completed ? '/perfectslice/leaderboard' : '/perfectslice/daily';
  let status = 'YOUR DAILY CHALLENGE', ariaLabel = 'Participate in The Daily Slice', action = 'PLAY NOW';
  let description = 'Same shape. Three cuts. Your best counts.';
  if (played) {
    status = 'YOUR NEXT CUT AWAITS'; ariaLabel = 'Continue The Daily Slice'; action = 'CONTINUE';
    description = `${played} of 3 cuts made. Your best counts.`;
  }
  if (completed) {
    status = 'COMPLETED TODAY'; ariaLabel = 'Daily Slice completed today — view leaderboard'; action = 'RESULTS';
    if (best) description = `${best.points} pts · ${formatPercent(best.difference)}% off${rank ? ` · #${rank} today` : ''}`;
  }
  return <section className={`cp-daily-slice-badge ${completed ? 'is-complete' : ''}`} data-testid="daily-slice-badge" aria-label="The Daily Slice">
    <Button asChild variant="ghost" className="cp-daily-slice-ticket"><a href={href} data-testid="home-play-daily-button" aria-label={ariaLabel} onClick={event => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); navigate(href); } }}>
      <span className="cp-daily-ticket-seal" aria-hidden="true"><PerfectSliceIcon/></span>
      <span className="cp-daily-ticket-copy"><span className="cp-daily-ticket-status" data-testid="daily-slice-badge-status-text">{completed && <Check size={12}/>} {status}</span><strong data-testid="daily-slice-badge-title">Participate in <span>The Daily Slice</span></strong><span className="cp-daily-ticket-description" data-testid="daily-slice-badge-description">{description}</span></span>
      <span className="cp-daily-ticket-stub"><span className="cp-daily-ticket-pips" aria-hidden="true">{[0, 1, 2].map(index => <i key={index} className={index < played ? 'is-used' : ''}/>)}</span><ArrowUpRight size={20}/><span data-testid="daily-slice-badge-action-label">{action}</span></span>
    </a></Button>
  </section>;
}
